/**
 * The date the tender's FIRST contract year ends — stated by the broker, not inferred.
 *
 * ⚠️ WHY THIS EXISTS: THE CLIENT'S OWN SPREADSHEETS DISAGREE WITH EACH OTHER.
 *
 * Every whole-of-term figure on page 5 and every savings line is the annual cost multiplied by
 * `term_weight`, and `term_weight` is driven by how long Year 1 runs. For a contract starting on
 * the 1st of a month there is no argument — every rule gives the same answer, and that is 983 of
 * prod's tenders. For a MID-MONTH start there are two defensible answers, and the client has
 * used both:
 *
 *     THE AMITY POINT COMMUNITY CLUB   starts 21 Oct 2026
 *       their test sheet   Year 1 ends 20 Oct 2027   364 days   weight 0.99726
 *       this portal        Year 1 ends 30 Sep 2027   344 days   weight 0.94247
 *
 *     COWES BAKEHOUSE                  starts 10 Apr 2026
 *       their sheet AND this portal    Year 1 ends 31 Mar 2027   355 days   weight 0.97260
 *
 * COWES and ANVERS pin the month-end anchor (see `clientTermWeightFromStart`); AMITY uses the
 * anniversary. No rule we can write satisfies both, because the disagreement is theirs. So the
 * broker states the date on the tender and the arithmetic follows it.
 *
 * ⚠️ THE OVERRIDE IS A DATE, NOT A DAY COUNT.
 *
 * `buildYearWeights` spreads the whole-of-term total EVENLY across its slots — deliberately, so
 * the environmental weighted mean stays right (see the note there). Year 1 therefore has no
 * weight of its own to set. One end DATE, on the other hand, fixes every term at once:
 *
 *     12 months -> that date          24 months -> that date + 12 months
 *     36 months -> that date + 24     and so on, on the tender's own series grid.
 *
 * ⚠️ `defaultYearOneEnd` REPRODUCES TODAY'S BEHAVIOUR EXACTLY.
 *
 * Fed back in, `termWeightFromYearOneEnd(start, defaultYearOneEnd(start, y1), term, y1)` returns
 * precisely what `clientTermWeightFromStart(start, term, y1)` returns — proven in
 * yearOneEnd.test.ts against that function across every start day of a year, on 12/24/36/48 and
 * on the 14/26/38/50 series. A tender that accepts the pre-filled date is a tender whose numbers
 * do not move.
 */

const MS_PER_DAY = 86_400_000;

/** The day count a term's weight is measured in, matching the client's `/ 365` throughout. */
const DAYS_PER_YEAR = 365;

/** A tender with no stated series has a twelve-month Year 1 — the same assumption as termWeight. */
const DEFAULT_YEAR1_MONTHS = 12;

const isFiniteMs = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const year1MonthsOf = (seriesYear1Months?: number | null): number =>
  Number.isFinite(seriesYear1Months) && (seriesYear1Months as number) > 0
    ? (seriesYear1Months as number)
    : DEFAULT_YEAR1_MONTHS;

/**
 * UTC midnight for a `YYYY-MM-DD` string, or null for anything else.
 *
 * Deliberately strict: a half-parsed date would move a tender's totals silently, which is the
 * one thing this feature must never do.
 */
export const parseIsoDay = (value: unknown): number | null => {
  if (typeof value !== 'string') return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const ms = Date.UTC(year, month - 1, day);
  const back = new Date(ms);
  // Rejects 31 Feb and friends, which Date.UTC would silently roll forward.
  if (back.getUTCFullYear() !== year || back.getUTCMonth() !== month - 1 || back.getUTCDate() !== day) {
    return null;
  }
  return ms;
};

/** `YYYY-MM-DD` for a UTC-midnight epoch, the form the override is stored in. */
export const toIsoDay = (ms: number | null | undefined): string =>
  isFiniteMs(ms) ? new Date(ms).toISOString().slice(0, 10) : '';

/**
 * The contract's first day: the day after the current contract expires.
 *
 * Mirrors `calculationRoutes.resolveContractStartDate` so the date shown on Step 1 is the same
 * one the engine prices from. Takes a Date, an ISO string or epoch ms, since the callers each
 * hold it differently.
 */
export const contractStartFromExpiry = (expiry: unknown): number | null => {
  if (expiry instanceof Date) {
    if (!Number.isFinite(expiry.getTime())) return null;
    return Date.UTC(expiry.getUTCFullYear(), expiry.getUTCMonth(), expiry.getUTCDate()) + MS_PER_DAY;
  }
  if (isFiniteMs(expiry)) {
    const d = new Date(expiry);
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) + MS_PER_DAY;
  }
  const iso = parseIsoDay(expiry);
  return iso === null ? null : iso + MS_PER_DAY;
};

/**
 * `base` moved on by `months`, keeping a month-end a month-end.
 *
 * ⚠️ A DATE THAT IS THE LAST DAY OF ITS MONTH STAYS THE LAST DAY OF ITS MONTH.
 *
 * The parity test caught this. `clientTermWeightFromStart` builds every end as
 * `Date.UTC(y, m + months, 1) - 1 day`, which is ALWAYS the last day of a month. Carrying the
 * day number across instead — 28 Feb 2027 plus twelve months as "the 28th" — lands on 28 Feb
 * 2028, but 2028 is a leap year and the existing engine says 29 Feb. One day out on a 24-month
 * term, on every tender starting in March of a year before a leap year.
 *
 * The clamp still matters for a date that is NOT a month-end: 31 Jan plus one month has no 31st
 * to land on, and `Date.UTC` would roll it into March.
 */
const addMonths = (base: number, months: number): number => {
  const d = new Date(base);
  const year = d.getUTCFullYear();
  const month = d.getUTCMonth();
  const day = d.getUTCDate();
  const lastOfBase = new Date(Date.UTC(year, month + 1, 1) - MS_PER_DAY).getUTCDate();
  const lastOfTarget = new Date(Date.UTC(year, month + months + 1, 1) - MS_PER_DAY).getUTCDate();
  const targetDay = day === lastOfBase ? lastOfTarget : Math.min(day, lastOfTarget);
  return Date.UTC(year, month + months, targetDay);
};

/**
 * The date Year 1 ends under the rule this portal uses today — the last day of the month BEFORE
 * the start month, a Year-1's length on.
 *
 * Same anchor as `clientTermWeightFromStart` and `computeYear1DayFraction`; this is what the
 * Step 1 field pre-fills with, so accepting it changes nothing.
 */
export const defaultYearOneEnd = (
  contractStartMs: number | null | undefined,
  seriesYear1Months?: number | null,
): number | null => {
  if (!isFiniteMs(contractStartMs)) return null;
  const start = new Date(contractStartMs);
  return Date.UTC(
    start.getUTCFullYear(), start.getUTCMonth() + year1MonthsOf(seriesYear1Months), 1,
  ) - MS_PER_DAY;
};

/**
 * The two rules a contract year can follow, plus the broker's own typed date.
 *
 * ⚠️ THE CLIENT USES BOTH, AND ONLY A MID-MONTH START CAN TELL THEM APART.
 *
 * A contract starting on the 1st ends Year 1 on the last day of the twelfth month either way —
 * 1,004 of prod's 1,113 dated tenders, where this type changes nothing at all. The 109 that
 * start mid-month are the whole of the question:
 *
 *     THE AMITY POINT COMMUNITY CLUB   starts 21 Oct 2026
 *       'anniversary'   Year 1 ends 20 Oct 2027   364 days   weight 0.99726
 *       'monthEnd'      Year 1 ends 30 Sep 2027   344 days   weight 0.94247
 *
 * AMITY's own sheet reads 20 Oct — the anniversary — and is the only one of the 109 anyone has
 * checked against a client workbook. 'monthEnd' is what this portal computes today, kept so a
 * tender that has always priced that way can keep doing so.
 */
export type YearOneEndRule = 'anniversary' | 'monthEnd' | 'explicit';

/** The rule a tender follows when it has not said otherwise. See YearOneEndRule. */
export const DEFAULT_YEAR_ONE_END_RULE: YearOneEndRule = 'anniversary';

/**
 * The date Year 1 ends under a given rule — the one form that works for a MULTI-SITE tender.
 *
 * ⚠️ A DATE CANNOT DESCRIBE SITES THAT START ON DIFFERENT DAYS, AND A RULE CAN.
 *
 * `tender.extn.contractYear.yearOneEnd` holds one date for the whole tender. That is sound while
 * every site starts together and meaningless the moment they do not: a site starting 01-07 and a
 * site starting 01-11 have different Year-1 ends, and whichever date is typed is wrong for one of
 * them. 35 tenders in prod have sites with different expiry dates.
 *
 * So the tender stores the RULE and each site resolves its own end from its own start. On a
 * single-site tender this returns exactly what the stored date would have, which is why the two
 * can coexist: 'explicit' means the broker typed a date and that date stands.
 *
 * Returns null for 'explicit' — the caller holds the typed date, not this function.
 */
export const yearOneEndForRule = (
  contractStartMs: number | null | undefined,
  rule: YearOneEndRule | null | undefined,
  seriesYear1Months?: number | null,
): number | null => {
  if (!isFiniteMs(contractStartMs)) return null;
  const resolved = rule ?? DEFAULT_YEAR_ONE_END_RULE;
  if (resolved === 'explicit') return null;
  if (resolved === 'monthEnd') return defaultYearOneEnd(contractStartMs, seriesYear1Months);
  // Anniversary: the day before the same date a Year-1's length on. addMonths keeps a month-end
  // start on the month end (31 Jan + 1 month is 28/29 Feb, not 3 March), so a contract starting
  // on the last day of a month still ends Year 1 on the last day of its own month.
  return addMonths(contractStartMs, year1MonthsOf(seriesYear1Months)) - MS_PER_DAY;
};

/** Whole days from the contract's first day to the stated end of Year 1. */
export const yearOneDayCount = (
  contractStartMs: number | null | undefined,
  yearOneEndMs: number | null | undefined,
): number | null => {
  if (!isFiniteMs(contractStartMs) || !isFiniteMs(yearOneEndMs)) return null;
  const days = Math.round((yearOneEndMs - contractStartMs) / MS_PER_DAY);
  return days > 0 ? days : null;
};

/**
 * The whole-of-term multiplier implied by a stated Year-1 end date.
 *
 * Identical arithmetic to `clientTermWeightFromStart` — raw days over 365, less one day from the
 * second contract year on — with the END taken from the broker's date rather than from the
 * month-end anchor. A term at or inside Year 1 keeps every day; anything beyond it loses the one
 * day the client's own Data Input Page deducts.
 */
export const termWeightFromYearOneEnd = (
  contractStartMs: number | null | undefined,
  yearOneEndMs: number | null | undefined,
  termMonths: number,
  seriesYear1Months?: number | null,
): number | null => {
  if (!isFiniteMs(contractStartMs) || !isFiniteMs(yearOneEndMs)) return null;
  if (!(termMonths > 0)) return null;

  const year1Months = year1MonthsOf(seriesYear1Months);
  const termEnd = addMonths(yearOneEndMs, termMonths - year1Months);
  const rawDays = Math.round((termEnd - contractStartMs) / MS_PER_DAY);
  if (!Number.isFinite(rawDays) || rawDays <= 0) return null;

  const isBeyondYear1 = termMonths > year1Months;
  return (rawDays - (isBeyondYear1 ? 1 : 0)) / DAYS_PER_YEAR;
};

/**
 * The `contractYear` block off a tender's `extn`, or null when it carries none.
 *
 * Tolerates `extn` arriving as a JSON string, which it does on some of the server's reads.
 */
const contractYearBlock = (extn: unknown): Record<string, unknown> | null => {
  let parsed: unknown = extn;
  if (typeof parsed === 'string') {
    try { parsed = JSON.parse(parsed); } catch { return null; }
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const block = (parsed as Record<string, unknown>).contractYear;
  if (!block || typeof block !== 'object') return null;
  return block as Record<string, unknown>;
};

/**
 * The stored Year-1 end for a tender, read straight off `tender.extn`.
 *
 * Stored at `extn.contractYear.yearOneEnd` as `YYYY-MM-DD`. Absent — which is every tender until
 * somebody sets one — returns null, and every caller then behaves exactly as it does today.
 */
export const yearOneEndFromExtn = (extn: unknown): number | null =>
  parseIsoDay(contractYearBlock(extn)?.yearOneEnd);

/**
 * The stored CONTRACT START for a tender, read straight off `tender.extn`.
 *
 * ⚠️ WHY A TENDER CARRIES ITS OWN START AT ALL.
 *
 * The engine derives the start from the sites — the day after the earliest
 * `current_contract_expiry`. A site that has never had a contract has no expiry to derive from,
 * and `validateSite` leaves expiry optional for exactly that reason, so a new connection reaches
 * the engine with no start at all and falls back to a flat 364/365 that nobody chose.
 *
 * Stored at `extn.contractYear.start` as `YYYY-MM-DD`, it is the broker's own answer and wins
 * over the derived one. Absent on every tender until somebody answers, and the derivation then
 * runs exactly as it does today.
 */
export const contractStartFromExtn = (extn: unknown): number | null =>
  parseIsoDay(contractYearBlock(extn)?.start);
